if (!customElements.get('christmas-gift-carousel')) {
  class ChristmasGiftCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.slides = Array.from(this.querySelectorAll('[data-gift-slide]'));
      this.previousButton = this.querySelector('[data-gift-previous]');
      this.nextButton = this.querySelector('[data-gift-next]');
      this.stage = this.querySelector('[data-gift-stage]');
      this.currentIndex = Math.min(
        Math.max(0, Number(this.dataset.initialSlide || 1) - 1),
        Math.max(0, this.slides.length - 1),
      );
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(2500, Number(this.dataset.autoplayDelay) || 6000);
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.touchStartX = null;
      this.autoplayTimer = 0;

      if (!this.stage || this.slides.length === 0) return;

      this.dataset.initialized = 'true';
      this.bindEvents();
      this.showSlide(this.currentIndex, false);
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.showSlide(this.currentIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.showSlide(this.currentIndex + 1, true));

      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', (event) => {
          if (index === this.currentIndex) return;
          event.preventDefault();
          this.showSlide(index, true);
        });
      });

      this.stage.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0]?.clientX ?? null;
        this.stopAutoplay();
      }, { passive: true });

      this.stage.addEventListener('touchend', (event) => {
        if (this.touchStartX === null) return;
        const endX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const distance = endX - this.touchStartX;
        this.touchStartX = null;

        if (Math.abs(distance) >= 45) {
          this.showSlide(this.currentIndex + (distance < 0 ? 1 : -1), true);
        } else {
          this.startAutoplay();
        }
      }, { passive: true });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showSlide(this.currentIndex - 1, true);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showSlide(this.currentIndex + 1, true);
        }
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => this.startAutoplay());
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });

      document.addEventListener('shopify:block:select', (event) => {
        const selectedSlide = event.target.closest?.('[data-gift-slide]');
        if (!selectedSlide || !this.contains(selectedSlide)) return;
        const index = this.slides.indexOf(selectedSlide);
        if (index >= 0) this.showSlide(index, false);
      });
    }

    normalizedDistance(index) {
      const total = this.slides.length;
      let distance = (index - this.currentIndex + total) % total;
      if (distance > total / 2) distance -= total;
      return distance;
    }

    showSlide(requestedIndex, restartAutoplay = false) {
      const total = this.slides.length;
      this.currentIndex = (requestedIndex + total) % total;

      this.slides.forEach((slide, index) => {
        const distance = this.normalizedDistance(index);
        const absoluteDistance = Math.abs(distance);
        const position = absoluteDistance > 2 ? 'hidden' : String(distance);
        const active = distance === 0;

        slide.dataset.position = position;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.setAttribute('aria-current', active ? 'true' : 'false');

        slide.querySelectorAll('a, button').forEach((control) => {
          if (active) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
      });

      this.setAttribute('aria-label', `Slide ${this.currentIndex + 1} of ${total}`);
      this.dispatchEvent(new CustomEvent('gift-carousel:change', {
        detail: { index: this.currentIndex },
      }));

      if (restartAutoplay) this.startAutoplay();
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = 0;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || this.reduceMotion || document.hidden || this.slides.length < 2) return;
      this.autoplayTimer = window.setInterval(() => this.showSlide(this.currentIndex + 1), this.autoplayDelay);
    }
  }

  customElements.define('christmas-gift-carousel', ChristmasGiftCarousel);
}
